/**
 * @file BatchDetails.jsx
 * @description Master view for a specific educational batch/classroom.
 *              Displays tests, practice problems, student leaderboards, and management tools
 *              for batch administrators.
 * @module components/BatchDetails
 */
import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete } from '../utils/apiUtils';
import { useAuth } from '../context/AuthContext';
import { FiArrowLeft, FiClock, FiFileText, FiCode, FiLock, FiBookOpen, FiAlertCircle, FiPlay, FiLogOut, FiMenu, FiX, FiLayers, FiAward, FiUsers, FiMail, FiPlus, FiTrash2, FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiMonitor, FiCheckCircle, FiCalendar, FiEdit3, FiArrowRight, FiCheck, FiCircle, FiExternalLink, FiSettings } from 'react-icons/fi';

import UserAvatar from './UserAvatar';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import TestManager from './TestManager';
import TestAttempt from './TestAttempt';
import TestResultsModal from './TestResultsModal';
import StudentTestResultsModal from './StudentTestResultsModal';
import AssignPracticeProblemModal from './AssignPracticeProblemModal';
import { motion, AnimatePresence } from 'framer-motion';

/* ══════════════════════════════════════════════════════════════
 * HELPERS & UTILS
 * ══════════════════════════════════════════════════════════════ */

const HourglassIcon = ({ className, strokeWidth = 2, ...props }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
    >
        <path d="M5 2h14" />
        <path d="M5 22h14" />
        <path d="M6 2v1c0 3.3 2.7 6 6 6s6-2.7 6-6V2" />
        <path d="M6 22v-1c0-3.3 2.7-6 6-6s6 2.7 6 6v1" />
    </svg>
);
const CountdownTimer = ({ targetDate, onStart }) => {
    const [timeLeft, setTimeLeft] = React.useState('');
    const [ended, setEnded] = React.useState(false);

    React.useEffect(() => {
        const update = () => {
            const now = new Date();
            const target = new Date(targetDate);
            const diff = target - now;
            if (diff <= 0) {
                setEnded(true);
                setTimeLeft('');
                return;
            }
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            if (h > 0) setTimeLeft(`${h}h ${m}m ${s}s`);
            else if (m > 0) setTimeLeft(`${m}m ${s}s`);
            else setTimeLeft(`${s}s`);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [targetDate]);

    if (ended) {
        return (
            <button
                onClick={onStart}
                className="w-full flex justify-center items-center space-x-2 bg-emerald-700 hover:bg-emerald-600 text-white py-2.5 transition-colors shadow-lg shadow-emerald-900/20"
            >
                <span className="font-semibold text-sm">Start Test</span>
            </button>
        );
    }

    return (
        <div className="w-full text-center bg-accent text-emerald-800 py-2.5 border border-emerald-200 text-sm font-semibold rounded">
            Starts in <span className="text-emerald-600">{timeLeft}</span>
        </div>
    );
};
const CountdownLabel = ({ targetDate, onExpire }) => {
    const [timeLeft, setTimeLeft] = React.useState('--');
    const expiredRef = React.useRef(false);

    React.useEffect(() => {
        expiredRef.current = false;
        const update = () => {
            if (!targetDate) {
                setTimeLeft('--');
                return;
            }
            const target = new Date(targetDate);
            if (isNaN(target.getTime())) {
                setTimeLeft('--');
                return;
            }
            const now = new Date();
            const diff = target - now;
            if (diff <= 0) {
                setTimeLeft('Starting...');
                if (!expiredRef.current && onExpire) {
                    expiredRef.current = true;
                    setTimeout(() => onExpire(), 1500);
                }
                return;
            }
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);

            if (h > 0) setTimeLeft(`${h}h ${m}m ${s}s`);
            else if (m > 0) setTimeLeft(`${m}m ${s}s`);
            else setTimeLeft(`${s}s`);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [targetDate, onExpire]);

    return (
        <div className="flex flex-col items-end">
            <span className="text-xs font-semibold text-emerald-600 tracking-wider mb-0.5">Test Starts In</span>
            <span className="text-sm font-bold text-slate-700 tabular-nums">
                {timeLeft}
            </span>
        </div>
    );
};
const CustomDateTimePicker = ({ value, onChange, required = false, disabled = false, placeholder = "YYYY-MM-DD HH:MM" }) => {
    return (
        <input
            type="text"
            required={required}
            value={value || ''}
            onChange={(e) => {
                const input = e.target.value;
                const digits = input.replace(/\D/g, '');
                let formatted = digits;
                if (digits.length > 4) {
                    formatted = digits.slice(0, 4) + '-' + digits.slice(4);
                }
                if (digits.length > 6) {
                    formatted = formatted.slice(0, 7) + '-' + digits.slice(6);
                }
                if (digits.length > 8) {
                    formatted = formatted.slice(0, 10) + ' ' + digits.slice(8);
                }
                if (digits.length > 10) {
                    formatted = formatted.slice(0, 13) + ':' + digits.slice(10, 12);
                }
                onChange(formatted);
            }}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full bg-accent border-b-2 border-transparent text-foreground px-4 py-3 
                focus:outline-none focus:border-emerald-600 transition-colors duration-200 text-sm
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-emerald-50'}`}
        />
    );
};

// Custom Select Component for theme consistency
const CustomSelect = ({ value, onChange, options, placeholder, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(
        options.find(opt => opt.value === value) || null
    );
    const dropdownRef = React.useRef(null);

    useEffect(() => {
        setSelectedOption(options.find(opt => opt.value === value) || null);
    }, [value, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        setSelectedOption(option);
        onChange(option.value);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full bg-white text-emerald-600 px-3 py-2 rounded
                focus:outline-none transition-all duration-200 flex items-center justify-between gap-4 font-medium text-sm ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-emerald-50'
                    }`}
            >
                <span className="truncate">
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                {!disabled && (
                    <div className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                        <FiChevronDown className="w-4 h-4 text-emerald-600" />
                    </div>
                )}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute left-0 right-0 mt-0.5 bg-white rounded z-50 max-h-60 overflow-y-auto divide-y divide-gray-200 drop-shadow-md"
                    >
                        {options.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => handleSelect(option)}
                                className={`w-full text-left py-2 px-3 text-sm font-medium transition-all duration-200
                                ${selectedOption?.value === option.value
                                        ? 'text-emerald-600'
                                        : 'text-foreground hover:bg-emerald-50'}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component BatchDetails
 * @description The main container for viewing and managing a single batch's context.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @param {string} props.batchId - The unique identifier for the batch.
 * @returns {JSX.Element} The rendered Batch Details view.
 */
const BatchDetails = ({ navigate, batchId }) => {
    // ── Component State ──
    const [batch, setBatch] = useState(null);
    const [practiceProblems, setPracticeProblems] = useState([]);
    const [tests, setTests] = useState([]);
    const [students, setStudents] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('tests'); // tests, practice, leaderboard, students
    const [studentsCurrentPage, setStudentsCurrentPage] = useState(1);
    const studentsPerPage = 10;

    // Practice Arena state
    const [showAssignPracticeModal, setShowAssignPracticeModal] = useState(false);
    const [practicePage, setPracticePage] = useState(1);

    // Test states
    const [testsPage, setTestsPage] = useState(1);
    const testsPerPage = 5;

    const [resultsTestModal, setResultsTestModal] = useState(null);
    const [studentResultsModal, setStudentResultsModal] = useState(null);

    // Leaderboard state
    const [leaderboard, setLeaderboard] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [leaderboardPage, setLeaderboardPage] = useState(1);
    const leaderboardPerPage = 10;

    const [error, setError] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Create Test Modal State
    const [showCreateTestModal, setShowCreateTestModal] = useState(false);
    const [newTestTitle, setNewTestTitle] = useState('');
    const [newTestDescription, setNewTestDescription] = useState('');
    const [newTestDuration, setNewTestDuration] = useState(60);
    const [newTestStartTime, setNewTestStartTime] = useState('');
    const [newTestEndTime, setNewTestEndTime] = useState('');
    const [newTestType, setNewTestType] = useState('mcq');
    const [creatingTest, setCreatingTest] = useState(false);

    // Manage Test State
    const [managingTest, setManagingTest] = useState(null);
    const [deletingTestId, setDeletingTestId] = useState(null);

    const [attemptingTest, setAttemptingTest] = useState(null);
    const [hasBatches, setHasBatches] = useState(false);

    // Tick state to force re-evaluation of test active/upcoming flags
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 5000);
        return () => clearInterval(interval);
    }, []);

    const { isAuthenticated, user, logout } = useAuth();


    useEffect(() => {
        const fetchBatchData = async () => {
            try {
                setLoading(true);
                // Fetch Batch Info
                const batchRes = await apiGet(`/batches/${batchId}`);
                if (!batchRes.ok) {
                    throw new Error("Failed to load batch. You might not have access.");
                }
                const batchData = await batchRes.json();
                setBatch(batchData.batch);
                setIsAdmin(!!batchData.isAdmin);

                // Set dynamic page title
                document.title = `SimpleCode | ${batchData.batch.name}`;

                const dataPromises = [
                    apiGet(`/batches/${batchId}/tests`),
                    apiGet(`/batches/${batchId}/practice-problems`)
                ];

                if (batchData.isAdmin) {
                    dataPromises.push(apiGet(`/batches/${batchId}/students`));
                }

                const [testsRes, practiceRes, studentsRes] = await Promise.all(dataPromises);

                // Fetch Tests
                if (testsRes && testsRes.ok) {
                    const testsData = await testsRes.json();
                    setTests(testsData.tests || []);
                }

                // Fetch Practice Problems
                if (practiceRes && practiceRes.ok) {
                    const practiceData = await practiceRes.json();
                    setPracticeProblems(practiceData.practiceProblems || []);
                }

                // Fetch Students
                if (batchData.isAdmin && studentsRes && studentsRes.ok) {
                    const studentsData = await studentsRes.json();
                    setStudents(studentsData.students || []);
                }

                // Check if user has batches for navigation
                const allBatchesRes = await apiGet('/batches');
                if (allBatchesRes.ok) {
                    const d = await allBatchesRes.json();
                    setHasBatches(d.batches && d.batches.length > 0);
                }

            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated && batchId) {
            fetchBatchData();
        }

        return () => {
            document.title = "SimpleCode";
        };
    }, [isAuthenticated, batchId]);

    // Fetch leaderboard data
    const fetchLeaderboard = async () => {
        if (!batchId) return;

        try {
            setLeaderboardLoading(true);
            const res = await apiGet(`/batches/${batchId}/leaderboard`);
            if (res.ok) {
                const data = await res.json();
                setLeaderboard(data.leaderboard || []);
            }
        } catch (err) {
            console.error('Error fetching leaderboard:', err);
        } finally {
            setLeaderboardLoading(false);
        }
    };

    // Fetch leaderboard data when tab changes
    useEffect(() => {
        if (activeTab === 'leaderboard') {
            fetchLeaderboard();
        }
    }, [activeTab, batchId]);

    const getDifficultyColor = (difficulty) => {
        switch (difficulty?.toLowerCase()) {
            case 'easy': return 'text-emerald-600';
            case 'medium': return 'text-yellow-600';
            case 'hard': return 'text-red-600';
            default: return 'text-gray-400';
        }
    };

    const isTestActive = (test) => {
        if (test.status === 'completed') return false;

        const now = new Date();
        const start = new Date(test.start_time);

        if (now < start) return false;

        if (!test.window_close_time) return true;

        const close = new Date(test.window_close_time);
        const absoluteEnd = new Date(close.getTime() + (test.duration_minutes || 0) * 60000);

        // Active if current time is within [start, absoluteEnd]
        return now <= absoluteEnd;
    };

    const isTestUpcoming = (test) => {
        const now = new Date();
        const start = new Date(test.start_time);
        const thirtyMinsBefore = new Date(start.getTime() - 30 * 60000); // 30 mins prior

        // Show test if current time is within 30 mins before start to just before start
        return now >= thirtyMinsBefore && now < start;
    };

    // Format a UTC ISO date string to IST display
    const formatIST = (isoString, options = {}) => {
        if (!isoString) return '—';
        return new Date(isoString).toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            hour: '2-digit', minute: '2-digit',
            day: '2-digit', month: 'short',
            ...options
        });
    };

    const handleCreateTest = async (e) => {
        e.preventDefault();
        setCreatingTest(true);
        try {
            // Convert local custom string to UTC ISO string to send to backend
            const parseDateString = (dateStr) => {
                if (!dateStr) return null;
                if (dateStr.length < 16) throw new Error("Please enter full date and time (YYYY-MM-DD HH:MM)");
                // Explicitly append IST offset (+05:30) so it's unambiguously parsed as IST
                const date = new Date(dateStr.replace(' ', 'T') + '+05:30');
                if (isNaN(date.getTime())) throw new Error("Invalid date/time provided");
                return date.toISOString();
            };

            const startISO = parseDateString(newTestStartTime);
            const endISO = parseDateString(newTestEndTime);

            if (startISO && endISO && new Date(endISO) <= new Date(startISO)) {
                throw new Error("End time must be after start time.");
            }

            const payload = {
                title: newTestTitle,
                description: newTestDescription,
                duration_minutes: Number(newTestDuration),
                start_time: startISO,
                window_close_time: endISO,
                test_type: newTestType
            };

            const response = await apiPost(`/batches/${batchId}/tests`, payload);
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to create test');
            }

            const data = await response.json();
            setTests([...tests, data.test].sort((a, b) => new Date(a.start_time) - new Date(b.start_time)));

            // Reset and close
            setShowCreateTestModal(false);
            setNewTestTitle('');
            setNewTestDescription('');
            setNewTestDuration(60);
            setNewTestStartTime('');
            setNewTestEndTime('');
            setNewTestType('mcq');
        } catch (err) {
            console.error('Create test error:', err);
            alert(err.message);
        } finally {
            setCreatingTest(false);
        }
    };

    const handleDeleteTest = async (testId) => {
        if (!window.confirm('Are you sure you want to delete this test? This will also remove all its questions.')) return;
        setDeletingTestId(testId);
        try {
            const response = await apiDelete(`/batches/${batchId}/tests/${testId}`);
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to delete test');
            }
            setTests(tests.filter(t => t.id !== testId));
        } catch (err) {
            console.error('Delete test error:', err);
            alert(err.message);
        } finally {
            setDeletingTestId(null);
        }
    };

    const handleRemovePracticeProblem = async (problemId) => {
        if (!window.confirm('Are you sure you want to remove this problem from the practice arena?')) return;
        try {
            const response = await apiDelete(`/batches/${batchId}/practice-problems/${problemId}`);
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to remove practice problem');
            }
            setPracticeProblems(prev => prev.filter(p => p.id !== problemId));
        } catch (err) {
            console.error('Remove practice problem error:', err);
            alert(err.message);
        }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center  text-white p-4">
                <div className="text-center max-w-md bg-zinc-900 p-8 rounded-2xl border border-red-500/30">
                    <FiAlertCircle className="text-5xl text-red-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
                    <p className="text-gray-400 mb-6">{error}</p>
                    <button onClick={() => navigate('/batches')} className="px-6 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors">Go Back</button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
                <Navbar navigate={navigate} />
                <main className="flex-1 py-16">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16">
                        <div className="h-6 w-32 bg-gray-200 animate-pulse mb-6 mt-10 rounded"></div>
                        <div className="mb-8">
                            <div className="h-12 w-1/3 bg-gray-200 animate-pulse mb-4 rounded"></div>
                            <div className="h-4 w-1/2 bg-gray-200 animate-pulse rounded"></div>
                        </div>
                        <div className="flex space-x-8 mt-8 border-b border-gray-200">
                            <div className="h-8 w-32 bg-gray-200 animate-pulse rounded"></div>
                            <div className="h-8 w-32 bg-gray-200 animate-pulse rounded"></div>
                        </div>
                        <div className="py-12">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[1, 2, 3].map(n => (
                                    <div key={n} className="bg-white border-l-4 border-emerald-600 p-6 shadow-sm rounded">
                                        <div className="h-4 w-16 bg-gray-100 mb-4 rounded"></div>
                                        <div className="h-6 w-3/4 bg-gray-100 mb-4 rounded"></div>
                                        <div className="h-4 w-full bg-gray-100 mb-2 rounded"></div>
                                        <div className="h-4 w-5/6 bg-gray-100 mb-6 rounded"></div>
                                        <div className="h-10 w-full bg-gray-100 rounded"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const handleNavClick = (path) => {
        navigate(path);
        closeMobileMenu();
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const getStudentsPageData = () => {
        const startIndex = (studentsCurrentPage - 1) * studentsPerPage;
        return students.slice(startIndex, startIndex + studentsPerPage);
    };

    const getTotalStudentsPages = () => Math.ceil(students.length / studentsPerPage);

    const getLeaderboardPageData = () => {
        const startIndex = (leaderboardPage - 1) * leaderboardPerPage;
        return leaderboard.slice(startIndex, startIndex + leaderboardPerPage);
    };

    const getTotalLeaderboardPages = () => Math.ceil(leaderboard.length / leaderboardPerPage);

    // Standardized links for Navbar & Footer
    const dashboardNavLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard") },
        { id: "classroom", label: "My Classroom", action: () => navigate("/batches"), show: hasBatches },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);


    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={dashboardNavLinks} />

            <div className="flex-1">
                {/* Mobile Restricted View */}
                <div className="md:hidden pt-32 pb-20 px-6 min-h-[70vh] flex flex-col items-center justify-center text-center">
                    <FiMonitor className="text-6xl text-emerald-600 mb-6 mx-auto" />
                    <h2 className="text-2xl font-bold text-foreground mb-3">Desktop Required</h2>
                    <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto text-center">
                        Please use a laptop or PC to access your batch details, manage students, and take assessments.
                    </p>
                    <button onClick={() => navigate('/dashboard')} className="mt-8 px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors font-medium mx-auto flex items-center gap-2">
                        <FiArrowLeft className="text-lg" />
                        Back to Dashboard
                    </button>
                </div>

                {/* Main Desktop Content */}
                <div className="hidden md:block bg-accent">
                    {/* Header below nav */}
                    {managingTest ? (
                        <div className="pt-24"></div>
                    ) : (
                    <div className="pt-24 pb-6">
                        <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16">
                            <div className="relative overflow-hidden flex flex-col md:flex-row md:items-start justify-between bg-emerald-700 rounded p-4 transition-all">
                                {/* Decorative Icon */}
                                <div className="absolute right-1 top-1 text-emerald-200 opacity-[0.3] transform -rotate-12 pointer-events-none transition-transform duration-500">
                                    <FiBookOpen className="w-40 h-40" />
                                </div>
                                <div className="text-left space-y-1">
                                    <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight leading-none mb-2">
                                        {batch?.name}
                                    </h1>
                                    <div className="flex items-center gap-3 text-white/80 py-0.5">
                                        <p className="text-lg font-semibold tracking-tight">{batch?.instructor || 'Instructor'}</p>
                                        <span className="text-white/80">|</span>
                                        <p className="text-lg font-semibold">
                                            {batch?.institution_name || 'SimpleCode'}
                                        </p>
                                    </div>
                                    <div className='pt-2'>
                                        <p className="text-sm font-medium text-emerald-200 tracking-wide italic">
                                            {batch?.description || 'Advanced Learning & Assessment Module'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Navigation Tabs */}
                            <div className="flex items-end space-x-1 mt-10 bg-white rounded p-4 shadow-md shadow-emerald-600/5">
                                <button
                                    onClick={() => { setActiveTab('tests'); setManagingTest(null); }}
                                    className={`relative px-6 py-3 text-sm font-medium tracking-wide transition-all duration-300 rounded flex items-center gap-2.5 group z-10 ${activeTab === 'tests' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    {activeTab === 'tests' && (
                                        <motion.div
                                            layoutId="batch-active-tab"
                                            className="absolute inset-0 bg-emerald-600 shadow-[0_-2px_10px_rgba(0,0,0,0.02)] rounded -z-10"
                                            initial={false}
                                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                                        />
                                    )}
                                    <FiFileText className={activeTab === 'tests' ? 'text-white scale-110' : 'group-hover:text-foreground transition-colors'} />
                                    <span>Assessments</span>
                                </button>

                                <button
                                    onClick={() => { setActiveTab('practice'); setManagingTest(null); }}
                                    className={`relative px-6 py-3 text-sm font-medium tracking-wide transition-all duration-300 rounded flex items-center gap-2.5 group z-10 ${activeTab === 'practice' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    {activeTab === 'practice' && (
                                        <motion.div
                                            layoutId="batch-active-tab"
                                            className="absolute inset-0 bg-emerald-600 shadow-[0_-2px_10px_rgba(0,0,0,0.02)] rounded -z-10"
                                            initial={false}
                                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                                        />
                                    )}
                                    <FiCode className={activeTab === 'practice' ? 'text-white scale-110' : 'group-hover:text-foreground transition-colors'} />
                                    <span>Practice Arena</span>
                                </button>


                                <button
                                    onClick={() => { setActiveTab('leaderboard'); setManagingTest(null); }}
                                    className={`relative px-6 py-3 text-sm font-medium tracking-wide transition-all duration-300 rounded flex items-center gap-2.5 group z-10 ${activeTab === 'leaderboard' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    {activeTab === 'leaderboard' && (
                                        <motion.div
                                            layoutId="batch-active-tab"
                                            className="absolute inset-0 bg-emerald-600 shadow-[0_-2px_10px_rgba(0,0,0,0.02)] rounded -z-10"
                                            initial={false}
                                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                                        />
                                    )}
                                    <FiAward className={activeTab === 'leaderboard' ? 'text-white scale-110' : 'group-hover:text-foreground transition-colors'} />
                                    <span>Leaderboard</span>
                                </button>

                                {isAdmin && (
                                    <button
                                        onClick={() => { setActiveTab('students'); setManagingTest(null); }}
                                        className={`relative px-6 py-3 text-sm font-medium tracking-wide transition-all duration-300 rounded flex items-center gap-2.5 group z-10 ${activeTab === 'students' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
                                    >
                                        {activeTab === 'students' && (
                                            <motion.div
                                                layoutId="batch-active-tab"
                                                className="absolute inset-0 bg-emerald-600 shadow-[0_-2px_10px_rgba(0,0,0,0.02)] rounded -z-10"
                                                initial={false}
                                                transition={{ type: "spring", stiffness: 350, damping: 30 }}
                                            />
                                        )}
                                        <FiUsers className={activeTab === 'students' ? 'text-white scale-110' : 'group-hover:text-foreground transition-colors'} />
                                        <span>Enrolled Students</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    )}

                    {activeTab === 'tests' && !managingTest && isAdmin && (
                        <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16">
                            <div className="flex justify-start">
                                <button
                                    onClick={() => setShowCreateTestModal(true)}
                                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-all font-medium text-sm flex items-center space-x-2"
                                >
                                    <FiPlus className="text-lg" />
                                    <span>Create New Test</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'practice' && !managingTest && isAdmin && (
                        <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16">
                            <div className="flex justify-start">
                                <button
                                    onClick={() => setShowAssignPracticeModal(true)}
                                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-all font-medium text-sm flex items-center space-x-2"
                                >
                                    <FiPlus className="text-lg" />
                                    <span>Assign Problems</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Content Body */}
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 pb-12 pb-24 mt-6">
                        {managingTest ? (
                            <TestManager test={managingTest} onBack={() => setManagingTest(null)} />
                        ) : (
                            <>
                                {activeTab === 'tests' && (() => {
                                    // Sort tests by start_time (newest first)
                                    const allTests = [...tests].sort((a, b) => new Date(b.start_time) - new Date(a.start_time));

                                    const totalItems = allTests.length;
                                    const itemsPerPage = testsPerPage;
                                    const totalPages = Math.ceil(totalItems / itemsPerPage);
                                    const currentPage = testsPage;
                                    const setCurrentPage = setTestsPage;


                                    const startIndex = (currentPage - 1) * itemsPerPage;
                                    const displayTests = allTests.slice(startIndex, startIndex + itemsPerPage);


                                    return (
                                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 bg-white rounded shadow-md shadow-emerald-600/5">

                                            {displayTests.length === 0 ? (
                                                <div className="text-center py-20 bg-white border-l-4 border-emerald-600 rounded">
                                                    <h3 className="text-xl font-bold text-muted-foreground mb-2">No Assessments Found</h3>
                                                    <p className="text-muted-foreground max-w-lg mx-auto text-sm font-medium">
                                                        There are currently no scheduled assessments at this moment
                                                    </p>

                                                </div>
                                            ) : (
                                                <div className="bg-white rounded border-l-4 border-emerald-600 shadow-sm flex flex-col overflow-hidden">


                                                    {/* Table Rows */}
                                                    <div className="divide-y divide-gray-200">
                                                        {displayTests.map(test => {
                                                            const active = isTestActive(test);
                                                            const upcoming = isTestUpcoming(test);
                                                            const now = new Date();
                                                            const windowExpired = test.window_close_time && new Date(test.window_close_time) < now;
                                                            const studentSubmitted = test.my_submission_status === 'completed' || test.my_submission_status === 'auto_submitted';
                                                            const studentInProgress = test.my_submission_status === 'in_progress';

                                                            let isAbsolutelyEnded = false;
                                                            if (test.window_close_time) {
                                                                const close = new Date(test.window_close_time);
                                                                const absEnd = new Date(close.getTime() + (test.duration_minutes || 0) * 60000);
                                                                if (now > absEnd) isAbsolutelyEnded = true;
                                                            }

                                                            const isEnded = ((test.status === 'completed' && !test.window_close_time) || isAbsolutelyEnded) || studentSubmitted;

                                                            return (
                                                                <div key={test.id} className="group flex flex-col sm:grid sm:grid-cols-12 items-center justify-between gap-1 sm:gap-4 px-4 sm:px-6 py-4 transition-colors relative overflow-hidden">
                                                                    {/* Column 1-4: Test Information */}
                                                                    <div className="sm:col-span-4 w-full min-w-0 flex items-start gap-3">
                                                                        <div className="min-w-0">
                                                                            <h3
                                                                                title={test.title}
                                                                                className="text-base font-semibold text-foreground group-hover:text-emerald-700 transition-colors truncate text-left"
                                                                            >
                                                                                {test.title}
                                                                            </h3>

                                                                            <div className="flex flex-wrap items-center gap-x-3 mt-0.5">
                                                                                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                                                                    <FiClock /> {test.duration_minutes} Mins
                                                                                </span>
                                                                                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                                                                    <FiCalendar /> {formatIST(test.start_time)} - {formatIST(test.window_close_time)}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Column 5-6: Type */}
                                                                    <div className="hidden sm:flex sm:col-span-2 items-center">
                                                                        <span className="text-sm font-medium text-muted-foreground capitalize">
                                                                            {test.test_type === 'mcq' ? 'MCQ' : test.test_type === 'code' ? 'Coding' : 'MCQ + Coding'}
                                                                        </span>
                                                                    </div>


                                                                    {/* Column 7-8: Topics */}
                                                                    <div className="hidden sm:flex sm:col-span-2 flex-wrap items-center gap-2">
                                                                        <span
                                                                            title={test?.description || ""}
                                                                            className='text-sm font-medium text-muted-foreground truncate'
                                                                        >
                                                                            {test?.description || "-"}
                                                                        </span>

                                                                    </div>


                                                                    <div className="hidden sm:flex sm:col-span-2 items-center justify-start text-left">
                                                                        {active && !isAdmin && studentInProgress ? (
                                                                            <span className="text-sm font-semibold text-amber-600 tracking-wide">In Progress</span>
                                                                        ) : active && !isAdmin && studentSubmitted ? (
                                                                            <span className="text-sm font-semibold text-emerald-600 tracking-wide">Attempted</span>
                                                                        ) : active ? (
                                                                            <span className="text-sm font-semibold text-red-600 tracking-wide animate-pulse">Live</span>
                                                                        ) : upcoming ? (
                                                                            <span className="text-sm font-semibold text-amber-600 tracking-wide">Starts Soon</span>
                                                                        ) : isEnded ? (
                                                                            isAdmin ? (
                                                                                <span className="text-sm font-semibold text-emerald-600 tracking-wide">Completed</span>
                                                                            ) : studentSubmitted ? (
                                                                                <span className="text-sm font-semibold text-emerald-600 tracking-wide">Attempted</span>
                                                                            ) : (
                                                                                <span className="text-sm font-semibold text-red-500 tracking-wide">Absent</span>
                                                                            )
                                                                        ) : (
                                                                            <span className="text-sm font-semibold text-slate-500 tracking-wide">Scheduled</span>
                                                                        )}
                                                                    </div>


                                                                    <div className="sm:col-span-2 w-full flex items-center justify-end gap-3">
                                                                        {!isAdmin ? (
                                                                            <>
                                                                                {active && studentInProgress ? (
                                                                                    <button 
                                                                                        onClick={() => setAttemptingTest(test)} 
                                                                                        className="w-[148px] px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm font-bold transition-all whitespace-nowrap flex items-center justify-center gap-2 shadow-sm"
                                                                                    >
                                                                                        Resume
                                                                                        <FiPlay className="text-sm" strokeWidth={3} />
                                                                                    </button>
                                                                                ) : active && studentSubmitted ? (
                                                                                    <span className="text-sm font-semibold text-slate-400">Test Results are Pending...</span>
                                                                                ) : active && !windowExpired && !studentSubmitted ? (
                                                                                    <button 
                                                                                        onClick={() => setAttemptingTest(test)} 
                                                                                        className="w-[148px] px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-bold transition-all whitespace-nowrap flex items-center justify-center gap-2 shadow-sm"
                                                                                    >
                                                                                        Start
                                                                                        <FiArrowRight className="text-sm" strokeWidth={3} />
                                                                                    </button>
                                                                                ) : isEnded ? (
                                                                                    test.results_published ? (
                                                                                        <button
                                                                                            onClick={() => setStudentResultsModal(test)}
                                                                                            className="w-[148px] px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-bold transition-all whitespace-nowrap flex items-center justify-center gap-2 shadow-sm"
                                                                                        >
                                                                                            View Results
                                                                                            <FiExternalLink className="text-sm" strokeWidth={3} />
                                                                                        </button>
                                                                                    ) : (
                                                                                        <span className="text-sm font-semibold text-slate-400">Test Results are Pending...</span>
                                                                                    )
                                                                                ) : upcoming ? (
                                                                                    <CountdownLabel targetDate={test.start_time} onExpire={() => setTick(t => t + 1)} />
                                                                                ) : windowExpired ? (
                                                                                    <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 whitespace-nowrap">
                                                                                        <FiLock className="w-3.5 h-3.5" />
                                                                                        Test Window Expired
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 whitespace-nowrap">
                                                                                        <FiLock className="w-3.5 h-3.5" />
                                                                                        Not Yet Available
                                                                                    </span>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <div className="flex items-center gap-2">
                                                                                <button
                                                                                    onClick={() => {
                                                                                        if (isEnded) {
                                                                                            setResultsTestModal(test);
                                                                                        } else if (!active) {
                                                                                            setManagingTest(test);
                                                                                        }
                                                                                    }}
                                                                                    disabled={active}
                                                                                    className={`w-[148px] px-4 py-3 rounded text-sm font-bold transition-all whitespace-nowrap flex items-center justify-center gap-2 ${active
                                                                                            ? ''
                                                                                            : isEnded
                                                                                                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                                                                                : 'bg-slate-600 hover:bg-slate-700 text-white'
                                                                                        }`}
                                                                                >
                                                                                    {isEnded ? (
                                                                                        <>
                                                                                            View Results
                                                                                            <FiExternalLink className="text-sm" strokeWidth={3} />
                                                                                        </>
                                                                                    ) : active ? '' : (
                                                                                        <>
                                                                                            Manage
                                                                                            <FiSettings className="text-sm" strokeWidth={3} />
                                                                                        </>
                                                                                    )}
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {/* Standardized Pagination */}
                                                    {totalPages > 1 && (
                                                        <div className="border-t border-border bg-emerald-50/20 px-6 py-4 rounded-b">
                                                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                                                <span className="text-sm font-medium text-muted-foreground">
                                                                    Showing <span className="text-foreground">{totalItems > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + itemsPerPage, totalItems)}</span> of <span className="text-foreground">{totalItems}</span> Assessments
                                                                </span>

                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                        disabled={currentPage === 1}
                                                                        className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                    >
                                                                        <FiChevronLeft className="w-4 h-4" />
                                                                    </button>
                                                                    <div className="flex items-center gap-1.5 px-2">
                                                                        <span className="font-semibold text-sm text-foreground">{currentPage}</span>
                                                                        <span className="text-muted-foreground text-xs font-medium px-1">/</span>
                                                                        <span className="text-muted-foreground text-sm font-medium">{totalPages}</span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                                        disabled={currentPage === totalPages}
                                                                        className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                    >
                                                                        <FiChevronRight className="w-4 h-4" />
                                                                    </button>

                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}


                                        </div>
                                    );
                                })()}

                                {activeTab === 'practice' && (() => {
                                    const totalItems = practiceProblems.length;
                                    const itemsPerPage = 10;
                                    const totalPages = Math.ceil(totalItems / itemsPerPage);
                                    const startIndex = (practicePage - 1) * itemsPerPage;
                                    const displayPractice = practiceProblems.slice(startIndex, startIndex + itemsPerPage);

                                    return (
                                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                            {displayPractice.length === 0 ? (
                                                <div className="text-center py-20 bg-white border-l-4 border-emerald-600 rounded shadow-md shadow-emerald-600/5">
                                                    <h3 className="text-xl font-bold text-muted-foreground mb-2">Workspace Empty</h3>
                                                    <p className="text-muted-foreground max-w-lg mx-auto text-sm font-medium">
                                                        {isAdmin
                                                            ? 'No practice problems have been assigned to this batch yet'
                                                            : 'No practice problems are available for this batch at the moment'}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="bg-white rounded border-l-4 border-emerald-600 shadow-sm flex flex-col overflow-hidden">
                                                    <div className="divide-y divide-border">
                                                        {displayPractice.map((problem) => (
                                                            <div
                                                                key={problem.id}
                                                                className={`group flex flex-col sm:grid sm:grid-cols-12 items-center justify-between gap-1 sm:gap-4 px-4 sm:px-6 py-4 transition-colors relative overflow-hidden ${!isAdmin ? 'cursor-pointer' : 'cursor-default'}`}
                                                            >
                                                                {/* ── MOBILE VIEW ── */}
                                                                <div className="flex sm:hidden items-center gap-3 w-full" onClick={() => !isAdmin && navigate(`/problems/${problem.id}`)}>
                                                                    {!isAdmin && (
                                                                        <span className="flex-shrink-0">
                                                                            {problem.is_solved ? (
                                                                                <div className="w-5 h-5 rounded-full flex items-center justify-center bg-emerald-600 text-white">
                                                                                    <FiCheck strokeWidth={3} className="w-3.5 h-3.5" />
                                                                                </div>
                                                                            ) : (
                                                                                <FiCircle className="text-muted-foreground w-5 h-5" />
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                    <h3 className="text-base font-semibold text-foreground truncate">
                                                                        <span className="text-emerald-700 font-bold mr-1.5">#{problem.id}</span>
                                                                        {problem.title}
                                                                    </h3>
                                                                </div>
                                                                <div className="flex sm:hidden items-center justify-between w-full mt-1.5 pl-8">
                                                                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                                                                        <span className="capitalize">{problem.topic?.toLowerCase() || 'general'}</span>
                                                                        <span className="opacity-30">|</span>
                                                                        <span className={`font-bold capitalize ${getDifficultyColor(problem.difficulty)}`}>{problem.difficulty}</span>
                                                                    </div>
                                                                    {!isAdmin ? (
                                                                        <div className="w-8 h-8 flex items-center justify-center text-muted-foreground" onClick={() => navigate(`/problems/${problem.id}`)}>
                                                                            <FiArrowRight className="w-4 h-4" />
                                                                        </div>
                                                                    ) : (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRemovePracticeProblem(problem.id);
                                                                            }}
                                                                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                            title="Remove from Practice Arena"
                                                                        >
                                                                            <FiTrash2 className="w-4 h-4" />
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                {/* ── DESKTOP VIEW ── */}
                                                                {/* Title / ID Col (col-span-5) */}
                                                                <div className="hidden sm:flex sm:col-span-5 items-start gap-3 min-w-0 pr-2" onClick={() => !isAdmin && navigate(`/problems/${problem.id}`)}>
                                                                    <span className="text-sm font-bold text-emerald-700 min-w-[40px] mt-0.5 flex-shrink-0">
                                                                        #{problem.id}
                                                                    </span>
                                                                    <div className="flex flex-col min-w-0">
                                                                        <h3 className={`text-base font-semibold text-foreground transition-colors truncate ${!isAdmin ? 'group-hover:text-emerald-700' : ''}`}>
                                                                            {problem.title}
                                                                        </h3>
                                                                    </div>
                                                                </div>

                                                                {/* Topic Col (col-span-3) */}
                                                                <div className="hidden sm:flex sm:col-span-3 items-center" onClick={() => !isAdmin && navigate(`/problems/${problem.id}`)}>
                                                                    <span className="text-xs font-medium text-muted-foreground capitalize">
                                                                        {problem.topic ? problem.topic.toLowerCase() : 'general'}
                                                                    </span>
                                                                </div>

                                                                {/* Difficulty Col (col-span-1) */}
                                                                <div className="hidden sm:flex sm:col-span-1 items-center" onClick={() => !isAdmin && navigate(`/problems/${problem.id}`)}>
                                                                    <span className={`text-xs font-semibold capitalize ${getDifficultyColor(problem.difficulty)}`}>
                                                                        {problem.difficulty}
                                                                    </span>
                                                                </div>

                                                                {/* Status Col (col-span-1) */}
                                                                <div className="hidden sm:flex sm:col-span-1 items-center justify-center" onClick={() => !isAdmin && navigate(`/problems/${problem.id}`)}>
                                                                    {!isAdmin && (
                                                                        problem.is_solved ? (
                                                                            <div className="w-6 h-6 rounded-full flex items-center justify-center bg-emerald-600 text-white" title="Solved">
                                                                                <FiCheck strokeWidth={3} className="w-4 h-4" />
                                                                            </div>
                                                                        ) : (
                                                                            <div className="w-6 h-6 rounded-full flex items-center justify-center" title="Unsolved">
                                                                                <FiCircle className="text-muted-foreground w-6 h-6" />
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>

                                                                {/* Action Col (col-span-2) */}
                                                                <div className="hidden sm:flex sm:col-span-2 items-center justify-end">
                                                                    {!isAdmin ? (
                                                                        <div className="w-8 h-8 flex items-center justify-center text-muted-foreground transition-colors" onClick={() => navigate(`/problems/${problem.id}`)}>
                                                                            <FiArrowRight className="transform group-hover:translate-x-0.5 transition-transform" />
                                                                        </div>
                                                                    ) : (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRemovePracticeProblem(problem.id);
                                                                            }}
                                                                            className="text-slate-500 hover:text-red-600 transition-colors"
                                                                            title="Remove from Practice Arena"
                                                                        >
                                                                            <FiTrash2 className="w-4 h-4" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Pagination */}
                                                    {totalPages > 1 && (
                                                        <div className="border-t border-border bg-emerald-50/20 px-6 py-4 rounded-b">
                                                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                                                <span className="text-sm font-medium text-muted-foreground">
                                                                    Showing <span className="text-foreground">{totalItems > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + itemsPerPage, totalItems)}</span> of <span className="text-foreground">{totalItems}</span> Problems
                                                                </span>

                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={() => setPracticePage(prev => Math.max(prev - 1, 1))}
                                                                        disabled={practicePage === 1}
                                                                        className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                    >
                                                                        <FiChevronLeft className="w-4 h-4" />
                                                                    </button>
                                                                    <div className="flex items-center gap-1.5 px-2">
                                                                        <span className="font-semibold text-sm text-foreground">{practicePage}</span>
                                                                        <span className="text-muted-foreground text-xs font-medium px-1">/</span>
                                                                        <span className="text-muted-foreground text-sm font-medium">{totalPages}</span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => setPracticePage(prev => Math.min(prev + 1, totalPages))}
                                                                        disabled={practicePage === totalPages}
                                                                        className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                    >
                                                                        <FiChevronRight className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}




                                {isAdmin && activeTab === 'students' && (
                                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                        {students.length === 0 ? (
                                            <div className="text-center py-20 bg-white border-l-4 border-emerald-600 rounded shadow-md shadow-emerald-600/5">
                                                <h3 className="text-xl font-bold text-muted-foreground mb-2">No Students Enrolled</h3>
                                                <p className="text-muted-foreground max-w-sm mx-auto text-sm font-medium">
                                                    Once students join this batch, they will appear in this list.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="bg-white rounded border-l-4 border-emerald-600 shadow-sm flex flex-col overflow-hidden">
                                                <div className="divide-y divide-emerald-50">
                                                    {getStudentsPageData().map(student => (
                                                        <div key={student.id} className="px-6 py-4 transition-colors duration-200 group grid grid-cols-1 sm:grid-cols-12 gap-4 items-center hover:bg-emerald-50/30">
                                                            {/* Avatar and Name */}
                                                            <div className="col-span-12 sm:col-span-5 flex items-center gap-4 min-w-0">
                                                                <UserAvatar
                                                                    avatarUrl={student.avatar_url}
                                                                    name={student.name || student.username}
                                                                    size={42}
                                                                />
                                                                <div className="truncate">
                                                                    <h3 className="text-foreground font-bold transition-colors truncate">
                                                                        {student.name || student.username || 'Unnamed Student'}
                                                                    </h3>
                                                                    <p className="text-xs text-muted-foreground font-medium tracking-tight text-left">@{student.username}</p>
                                                                </div>
                                                            </div>

                                                            <div className="col-span-12 sm:col-span-4">
                                                                <span className="text-sm font-medium text-emerald-600/80 truncate block text-left" title={student.institute_email}>
                                                                    {student.institute_email || '—'}
                                                                </span>
                                                            </div>

                                                            <div className="col-span-12 sm:col-span-1 text-center">
                                                                <span className="text-sm font-bold text-muted-foreground transition-colors">
                                                                    {student.institute_roll_no || '—'}
                                                                </span>
                                                            </div>

                                                            <div className="col-span-12 sm:col-span-2 text-left sm:text-right">
                                                                <span className="text-sm font-medium text-muted-foreground">
                                                                    Enrolled on {new Date(student.enrolled_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Standardized Pagination */}
                                                {getTotalStudentsPages() > 1 && (
                                                    <div className="border-t border-border bg-emerald-50/20 px-6 py-4 rounded-b">
                                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                                            <span className="text-sm font-medium text-muted-foreground">
                                                                Showing <span className="text-foreground">{students.length > 0 ? (studentsCurrentPage - 1) * studentsPerPage + 1 : 0}-{Math.min(studentsCurrentPage * studentsPerPage, students.length)}</span> of <span className="text-foreground">{students.length}</span> Students
                                                            </span>

                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => setStudentsCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                    disabled={studentsCurrentPage === 1}
                                                                    className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                >
                                                                    <FiChevronLeft className="w-4 h-4" />
                                                                </button>
                                                                <div className="flex items-center gap-1.5 px-2">
                                                                    <span className="font-semibold text-sm text-foreground">{studentsCurrentPage}</span>
                                                                    <span className="text-muted-foreground text-xs font-medium px-1">/</span>
                                                                    <span className="text-muted-foreground text-sm font-medium">{getTotalStudentsPages()}</span>
                                                                </div>
                                                                <button
                                                                    onClick={() => setStudentsCurrentPage(prev => Math.min(prev + 1, getTotalStudentsPages()))}
                                                                    disabled={studentsCurrentPage === getTotalStudentsPages()}
                                                                    className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                >
                                                                    <FiChevronRight className="w-4 h-4" />
                                                                </button>

                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'leaderboard' && (
                                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                        {leaderboardLoading ? (
                                            <div className="space-y-8 animate-pulse">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    {[1, 2, 3].map((i) => (
                                                        <div key={i} className="p-8 bg-white border border-emerald-100 rounded flex flex-col items-center">
                                                            <div className="w-20 h-20 rounded-full bg-emerald-50 mb-4"></div>
                                                            <div className="h-4 w-24 bg-emerald-50 rounded mb-2"></div>
                                                            <div className="h-4 w-16 bg-emerald-50 rounded"></div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="h-64 bg-white border border-emerald-100 rounded"></div>
                                            </div>
                                        ) : leaderboard.length === 0 ? (
                                            <div className="text-center py-20 bg-white border-l-4 border-emerald-600 rounded shadow-md shadow-emerald-600/5">
                                                <h3 className="text-xl font-bold text-muted-foreground mb-2">Leaderboard Pending</h3>
                                                <p className="text-muted-foreground max-w-lg mx-auto text-sm font-medium">
                                                    Rankings will appear once test results are published for this batch
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-12">
                                                {/* Top 3 Premium Cards */}
                                                {leaderboardPage === 1 && (
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-end">
                                                        {(() => {
                                                            const top3 = leaderboard.slice(0, 3);
                                                            const ordered = [top3[1], top3[0], top3[2]].filter(x => x);
                                                            return ordered.map((student) => {
                                                                const isFirst = student.rank === 1;
                                                                return (
                                                                    <div key={student.user_id} className={`relative p-8 bg-emerald-800 rounded-3xl transition-all duration-500 group overflow-hidden ${isFirst ? 'md:pb-12 shadow-xl shadow-emerald-900/20' : 'md:pb-8 shadow-lg'}`}>
                                                                        <div className="relative z-10 flex flex-col items-center text-center">
                                                                            {/* Avatar Wrapper with Perfect Metallic Borders */}
                                                                            <div className="relative mb-3">
                                                                                <div
                                                                                    className={`rounded-full flex items-center justify-center`}
                                                                                    style={{ width: isFirst ? 116 : 96, height: isFirst ? 116 : 96 }}
                                                                                >
                                                                                    <UserAvatar avatarUrl={student.avatar_url} name={student.name} size={isFirst ? 100 : 80} />
                                                                                </div>
                                                                                {/* Integrated Rank Badge */}
                                                                                <div className={`absolute top-0 right-0 w-9 h-9 rounded-full flex items-center justify-center font-black text-sm z-20 ${isFirst ? 'bg-amber-400 text-amber-900' : student.rank === 2 ? 'bg-slate-200 text-slate-600' : 'bg-orange-300 text-orange-900'}`}>
                                                                                    #{student.rank}
                                                                                </div>
                                                                            </div>

                                                                            <h3 className="text-xl font-medium text-white mb-1 truncate w-full">{student.name}</h3>
                                                                            <p className="text-sm text-emerald-100 mb-3">@{student.username}</p>

                                                                            <div className="mb-2">
                                                                                <span className="text-white text-xl font-medium">{student.average_percentage}%</span>
                                                                            </div>

                                                                            <div className="grid grid-cols-3 gap-4 w-full pt-6 border-t border-white/50 mt-1">
                                                                                <div className="text-center border-r border-white/50 pr-4">
                                                                                    <p className="text-xs text-emerald-100 font-semibold mb-1">Total Score</p>
                                                                                    <p className="text-sm font-bold text-white">{student.total_obtained}/{student.total_possible}</p>
                                                                                </div>
                                                                                <div className="text-center border-r border-white/50 pr-4">
                                                                                    <p className="text-xs text-emerald-100 font-semibold mb-1">Avg Time Taken</p>
                                                                                    <p className="text-sm font-bold text-white">{student.avg_time_percentage}%</p>
                                                                                </div>
                                                                                <div className="text-center pl-4">
                                                                                    <p className="text-xs text-emerald-100 font-semibold mb-1">Tests Attempted</p>
                                                                                    <p className="text-sm font-bold text-white">{student.tests_attempted}/{student.total_tests}</p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                )}

                                                {/* Detailed Table */}
                                                <div className="bg-white rounded border-l-4 border-emerald-600 shadow-sm flex flex-col overflow-hidden">
                                                    <div className="divide-y divide-emerald-50">
                                                        {getLeaderboardPageData().map(student => (
                                                            <div key={student.user_id} className="px-6 py-5 transition-colors duration-200 group lg:grid grid-cols-12 items-center hover:bg-emerald-50/30 gap-4">
                                                                <div className="col-span-1 flex items-center lg:justify-start">
                                                                    <span className={`text-xl font-black ${student.rank === 1 ? 'text-yellow-600' : student.rank === 2 ? 'text-slate-600' : student.rank === 3 ? 'text-orange-600' : 'text-emerald-600'}`}>
                                                                        #{student.rank}
                                                                    </span>
                                                                </div>

                                                                <div className="col-span-5 flex items-center gap-4 min-w-0">
                                                                    <UserAvatar avatarUrl={student.avatar_url} name={student.name} size={42} />
                                                                    <div className="truncate text-left">
                                                                        <div className="text-foreground font-bold transition-colors truncate">{student.name}</div>
                                                                        <div className="text-xs text-muted-foreground font-medium tracking-tight">@{student.username}</div>
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    className="col-span-4 text-sm font-medium text-emerald-600 truncate text-left"
                                                                    title={student.institute_email}
                                                                >
                                                                    {student.institute_email}
                                                                </div>

                                                                <div className="col-span-2 text-right flex flex-col items-end">
                                                                    <div className="text-emerald-600 font-extrabold text-xl leading-none">{student.average_percentage}%</div>
                                                                    <div className="text-sm text-muted-foreground font-medium mt-1">{student.total_obtained} pts</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Standardized Pagination */}
                                                    {getTotalLeaderboardPages() > 1 && (
                                                        <div className="border-t border-border bg-emerald-50/20 px-6 py-4 rounded-b">
                                                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                                                <span className="text-sm font-medium text-muted-foreground">
                                                                    Showing <span className="text-foreground">{leaderboard.length > 0 ? (leaderboardPage - 1) * leaderboardPerPage + 1 : 0}-{Math.min(leaderboardPage * leaderboardPerPage, leaderboard.length)}</span> of <span className="text-foreground">{leaderboard.length}</span> Ranks
                                                                </span>

                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={() => setLeaderboardPage(prev => Math.max(prev - 1, 1))}
                                                                        disabled={leaderboardPage === 1}
                                                                        className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                    >
                                                                        <FiChevronLeft className="w-4 h-4" />
                                                                    </button>
                                                                    <div className="flex items-center gap-1.5 px-2">
                                                                        <span className="font-semibold text-sm text-foreground">{leaderboardPage}</span>
                                                                        <span className="text-muted-foreground text-xs font-medium px-1">/</span>
                                                                        <span className="text-muted-foreground text-sm font-medium">{getTotalLeaderboardPages()}</span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => setLeaderboardPage(prev => Math.min(prev + 1, getTotalLeaderboardPages()))}
                                                                        disabled={leaderboardPage === getTotalLeaderboardPages()}
                                                                        className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                    >
                                                                        <FiChevronRight className="w-4 h-4" />
                                                                    </button>

                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Create Test Modal */}
                    {
                        showCreateTestModal && isAdmin && (
                            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-emerald-950/50 backdrop-blur-md overflow-y-auto">
                                <div className="bg-white w-full max-w-2xl mx-auto my-8 flex flex-col max-h-[85vh] shadow-2xl rounded">
                                    {/* Header */}
                                    <div className="flex items-start justify-between p-6 py-4 bg-emerald-600 text-left rounded-t">
                                        <div>
                                            <h3 className="text-xl font-bold text-white mb-0.5">Create New Test</h3>
                                            <p className="text-xs text-emerald-100">Initialize a new test configuration</p>
                                        </div>
                                        <button onClick={() => setShowCreateTestModal(false)} className="text-emerald-100 hover:text-white transition-colors mt-1">
                                            <FiX className="text-2xl" />
                                        </button>
                                    </div>

                                    {/* Body - Scrollable */}
                                    <div className="p-8 overflow-y-auto flex-1 custom-scrollbar">
                                        <form id="create-test-form" onSubmit={handleCreateTest} className="space-y-6 text-left">
                                            <div>
                                                <label className="block text-sm font-semibold text-emerald-800 mb-2">Test Title <span className="text-rose-500">*</span></label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={newTestTitle}
                                                    onChange={(e) => setNewTestTitle(e.target.value)}
                                                    className="w-full bg-accent border-b-2 border-transparent rounded-t text-foreground px-4 py-3 focus:outline-none focus:border-emerald-600 transition-colors duration-200 text-sm font-medium placeholder:text-muted-foreground/40"
                                                    placeholder="e.g. Midterm Objective Exam"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-emerald-800 mb-2">Test Type <span className="text-rose-500">*</span></label>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                                    <label className="relative flex items-center p-3 cursor-pointer group">
                                                        <input
                                                            type="radio"
                                                            value="mcq"
                                                            checked={newTestType === 'mcq'}
                                                            onChange={() => setNewTestType('mcq')}
                                                            className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 mr-3 cursor-pointer"
                                                        />
                                                        <div className="flex flex-col">
                                                            <span className={`font-bold text-sm transition-colors ${newTestType === 'mcq' ? 'text-emerald-600' : 'text-muted-foreground group-hover:text-foreground'}`}>MCQ</span>
                                                        </div>
                                                    </label>

                                                    <label className="relative flex items-center p-3 cursor-pointer group">
                                                        <input
                                                            type="radio"
                                                            value="code"
                                                            checked={newTestType === 'code'}
                                                            onChange={() => setNewTestType('code')}
                                                            className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 mr-3 cursor-pointer"
                                                        />
                                                        <div className="flex flex-col">
                                                            <span className={`font-bold text-sm transition-colors ${newTestType === 'code' ? 'text-emerald-600' : 'text-muted-foreground group-hover:text-foreground'}`}>Code-Based</span>
                                                        </div>
                                                    </label>

                                                    <label className="relative flex items-center p-3 cursor-pointer group">
                                                        <input
                                                            type="radio"
                                                            value="mixed"
                                                            checked={newTestType === 'mixed'}
                                                            onChange={() => setNewTestType('mixed')}
                                                            className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 mr-3 cursor-pointer"
                                                        />
                                                        <div className="flex flex-col">
                                                            <span className={`font-bold text-sm transition-colors ${newTestType === 'mixed' ? 'text-emerald-600' : 'text-muted-foreground group-hover:text-foreground'}`}>Mixed Test</span>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-emerald-800 mb-2">Description</label>
                                                <textarea
                                                    value={newTestDescription}
                                                    onChange={(e) => setNewTestDescription(e.target.value)}
                                                    className="w-full bg-accent border-b-2 border-transparent rounded-t text-foreground px-4 py-3 focus:outline-none focus:border-emerald-600 transition-colors duration-200 h-24 custom-scrollbar text-sm font-medium placeholder:text-muted-foreground/40 resize-none"
                                                    placeholder="Instructions or details about the test..."
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-semibold text-emerald-800 mb-2">Start Time <span className="text-rose-500">*</span></label>
                                                    <CustomDateTimePicker
                                                        required
                                                        value={newTestStartTime}
                                                        onChange={(val) => setNewTestStartTime(val)}
                                                    />
                                                    <p className="text-[10px] text-muted-foreground font-medium mt-1">When students can see the test</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-emerald-800 mb-2">End Time (Deadline) <span className="text-rose-500">*</span></label>
                                                    <CustomDateTimePicker
                                                        required
                                                        value={newTestEndTime}
                                                        onChange={(val) => setNewTestEndTime(val)}
                                                    />
                                                    <p className="text-[10px] text-muted-foreground font-medium mt-1">When the test closes for entry</p>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-emerald-800 mb-2">Duration (Minutes) <span className="text-rose-500">*</span></label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="300"
                                                    required
                                                    value={newTestDuration}
                                                    onChange={(e) => setNewTestDuration(e.target.value)}
                                                    className="w-full md:w-1/2 bg-accent rounded-t border-b-2 border-transparent text-foreground px-4 py-3 focus:outline-none focus:border-emerald-600 transition-colors duration-200 text-sm font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    placeholder="60"
                                                />
                                                <p className="text-[10px] text-muted-foreground font-medium mt-1">Time allowed once the student begins the test</p>
                                            </div>
                                        </form>
                                    </div>

                                    {/* Footer */}
                                    <div className="px-4 py-6 border-t border-gray-200 flex justify-end items-center space-x-4 bg-emerald-50/10 rounded-b-xl">
                                        <button
                                            type="button"
                                            onClick={() => setShowCreateTestModal(false)}
                                            disabled={creatingTest}
                                            className="px-6 py-2.5 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            form="create-test-form"
                                            disabled={creatingTest}
                                            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white transition-all font-bold rounded flex items-center disabled:opacity-50 text-sm"
                                        >
                                            {creatingTest ? (
                                                <><span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></span> Creating...</>
                                            ) : "Create Test"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div >
            </div >

            {/* Test Attempt Overlay */}
            {
                attemptingTest && (
                    <TestAttempt
                        test={attemptingTest}
                        onComplete={() => {
                            setAttemptingTest(null);
                            apiGet(`/batches/${batchId}/tests`).then(r => r.json()).then(data => {
                                if (data.tests) setTests(data.tests);
                            }).catch(() => { });
                        }}
                    />
                )
            }
            {/* Admin Test Results Modal */}
            {
                resultsTestModal && (
                    <TestResultsModal
                        test={resultsTestModal}
                        onClose={() => setResultsTestModal(null)}
                        onPublishSuccess={(testId) => {
                            setTests(tests.map(t => t.id === testId ? { ...t, results_published: true } : t));
                            setResultsTestModal(prev => ({ ...prev, results_published: true }));
                            // Auto-refresh leaderboard data if it's already been loaded
                            fetchLeaderboard();
                        }}
                    />
                )
            }

            {/* Student Test Results Modal */}
            {
                studentResultsModal && (
                    <StudentTestResultsModal
                        test={studentResultsModal}
                        onClose={() => setStudentResultsModal(null)}
                    />
                )
            }

            {/* Admin Assign Practice Problem Modal */}
            {
                showAssignPracticeModal && (
                    <AssignPracticeProblemModal
                        batchId={batchId}
                        onClose={() => setShowAssignPracticeModal(false)}
                        onProblemAssigned={(newProblem) => {
                            setPracticeProblems(prev => [newProblem, ...prev]);
                            setShowAssignPracticeModal(false);
                        }}
                    />
                )
            }
            <Footer navLinks={footerLinks} />
        </div>
    );
};

export default BatchDetails;
